/**
 * バージョン比較関数
 * 
 * 指定されたバージョンが利用規約の最小要求バージョンを満たしているかを判定する。
 * セマンティックバージョニング（major.minor.patch）形式のバージョン文字列を比較する。
 * 
 * @param currentVersion - チェック対象のバージョン文字列（例: "1.2.3"）。undefinedの場合はfalseを返す
 * @param minimumVersion - 最小要求バージョン文字列（例: "1.2.0"）
 * @returns 指定されたバージョンが最小要求バージョン以上の場合はtrue、そうでなければfalse
 * 
 * @example
 * ```typescript
 * checkVersion("1.2.3", "1.2.2") // trueを返す
 * checkVersion("1.1.9", "1.2.0") // falseを返す
 * checkVersion(undefined, "1.2.0") // falseを返す
 * checkVersion("1.2", "1.2.0") // 不正な形式のため、falseを返す
 * checkVersion("1.2.3.4", "1.2.0") // 不正な形式のため、falseを返す
 * checkVersion("a.b.c", "1.2.0") // 不正な形式のため、falseを返す
 * ```
 * 
 * @description
 * バージョン比較のロジック：
 * 1. バージョン文字列が X.X.X 形式でない場合はfalse
 * 2. 各要素が数値でない場合はfalse
 * 3. メジャーバージョンが大きい場合はtrue
 * 4. メジャーバージョンが同じでマイナーバージョンが大きい場合はtrue
 * 5. メジャー・マイナーバージョンが同じでパッチバージョンが同じ以上の場合はtrue
 * 6. その他の場合はfalse
 */
export const checkVersion = (currentVersion: string | undefined, minimumVersion: string): boolean => {
    if (!currentVersion) return false;
    
    // X.X.X 形式のチェック
    const versionParts = currentVersion.split('.');
    if (versionParts.length !== 3) return false;
    
    // 各要素が数値かチェック
    const [majorA, minorA, patchA] = versionParts.map(Number);
    if (isNaN(majorA) || isNaN(minorA) || isNaN(patchA)) return false;

    const [majorB, minorB, patchB] = minimumVersion.split('.').map(Number);

    if (majorA > majorB) return true;
    if (majorA === majorB && minorA > minorB) return true;
    if (majorA === majorB && minorA === minorB && patchA >= patchB) return true;
    return false;
};